import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { createContext, useCallback, useContext, useMemo, useState, type PropsWithChildren } from 'react'
import { DMP_CHAT_AGENT_KEY } from '../dmpChatContract'

export type SelectedDmpChatAgent = {
  id: number
  key: string
  name: string
  canEdit: boolean
}

export type DmpChatAvailableAgent = RestSchemas['Messages.UserAgent.UserAgentSummary']

type DmpChatAgentContextValue = {
  selectedAgent: SelectedDmpChatAgent | null
  setSelectedAgent: (agent: SelectedDmpChatAgent | null) => void
  agentKey: string
  agents: readonly DmpChatAvailableAgent[] | undefined
  agentsLoading: boolean
  selectAgentByKey: (key: string) => boolean
}

const DmpChatAgentContext = createContext<DmpChatAgentContextValue | null>(null)

const fallbackAgentKey = DMP_CHAT_AGENT_KEY

export function DmpChatAgentProvider({ children, projectId }: PropsWithChildren<{ projectId: number }>) {
  const [selectedAgent, setSelectedAgent] = useState<SelectedDmpChatAgent | null>(null)
  const { getUserAgentApiGetUserAgents } = useRestClient()
  const { data: agents, isLoading: agentsLoading } = getUserAgentApiGetUserAgents.useQuery({
    query: { projectId },
  })
  const selectAgentByKey = useCallback(
    (key: string) => {
      const agent = agents?.find((entry) => entry.config?.key === key)
      if (!agent?.id || !agent.config?.key) {
        return false
      }

      setSelectedAgent({
        id: agent.id,
        key: agent.config.key,
        name: agent.name ?? agent.config.name ?? agent.config.key,
        canEdit: !!agent.canEdit,
      })
      return true
    },
    [agents]
  )

  const value = useMemo<DmpChatAgentContextValue>(
    () => ({
      selectedAgent,
      setSelectedAgent,
      agentKey: selectedAgent?.key ?? fallbackAgentKey,
      agents,
      agentsLoading,
      selectAgentByKey,
    }),
    [agents, agentsLoading, selectedAgent, selectAgentByKey]
  )

  return <DmpChatAgentContext.Provider value={value}>{children}</DmpChatAgentContext.Provider>
}

export function useDmpChatAgent(): DmpChatAgentContextValue {
  const context = useContext(DmpChatAgentContext)
  if (!context) {
    throw new Error('useDmpChatAgent must be used inside DmpChatAgentProvider')
  }

  return context
}
